import { useState } from 'react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MapPin, Phone, Instagram, Linkedin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export type ContactSubmission = {
  id?: string
  name: string
  email: string
  subject: string
  message: string
  created_at?: string
}

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  subject: z.string().trim().min(1, "Subject is required").max(200, "Subject must be less than 200 characters"),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters")
});

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate form data with zod
      const validatedData = contactSchema.parse(formData);

      // Client-side throttle: at most one message per minute from this browser
      const lastSentAt = Number(localStorage.getItem('contact_last_sent_at') ?? 0);
      if (Date.now() - lastSentAt < 60_000) {
        throw new Error('Rate limit exceeded');
      }

      const { error } = await supabase.rpc('submit_contact_form', {
        _name: validatedData.name,
        _email: validatedData.email,
        _subject: validatedData.subject,
        _message: validatedData.message,
      });

      if (error) {
        throw error;
      }

      localStorage.setItem('contact_last_sent_at', String(Date.now()));
      toast({
        title: "Message Sent Successfully",
        description: "Thank you for your message. I'll get back to you soon!",
      });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      
      if (error instanceof z.ZodError) {
        // Handle validation errors
        const firstError = error.issues[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
      } else {
        const message = (error as { message?: string })?.message ?? '';
        const rateLimited = message.includes('Rate limit exceeded');

        toast({
          title: rateLimited ? "Too Many Messages" : "Submission Failed",
          description: rateLimited
            ? "You've sent several messages recently. Please wait a little while before sending another, or email info@terrrevia.com."
            : "We couldn't send your message right now. Please try again shortly, or email info@terrrevia.com.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="py-12 sm:py-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-12 sm:mb-20">
        <div className="max-w-4xl mx-auto text-center text-reveal">
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-primary mb-4 sm:mb-6">
            Get in Touch
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed px-4">
            For exhibition enquiries, collaborations, or commissions, please contact Terravia Ceramics
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="card-gradient p-6 sm:p-8 shadow-medium">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-primary mb-6">
              Send a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-base">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 h-12 text-base"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 h-12 text-base"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="subject" className="text-base">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="mt-2 h-12 text-base"
                  placeholder="What is this regarding?"
                />
              </div>
              
              <div>
                <Label htmlFor="message" className="text-base">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2 min-h-32 text-base"
                  placeholder="Please share details about your enquiry..."
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full hover-lift h-12 text-base" 
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
            
            <p className="text-xs text-muted-foreground mt-4">
              * Required fields. Your information will be kept private and used only to respond to your enquiry.
            </p>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6 lg:space-y-8">
            <Card className="card-gradient p-6 sm:p-8 shadow-soft">
              <h3 className="font-display text-lg sm:text-xl font-semibold mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Email</h4>
                    <p className="text-muted-foreground text-sm sm:text-base">info@terrrevia.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Phone className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Phone</h4>
                    <p className="text-muted-foreground text-sm sm:text-base">+44 7423 202954</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Available during studio hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">UK Studio</h4>
                    <p className="text-muted-foreground text-sm sm:text-base">116, Miller Height</p>
                    <p className="text-muted-foreground text-sm sm:text-base">Maidstone, ME15 6LZ</p>
                    <p className="text-muted-foreground text-sm sm:text-base">United Kingdom</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Studio visits by appointment</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Nigeria Studio</h4>
                    <p className="text-muted-foreground text-sm sm:text-base">No 54, Kamaldeen Street, Okekere</p>
                    <p className="text-muted-foreground text-sm sm:text-base">Ilorin, Kwara State</p>
                    <p className="text-muted-foreground text-sm sm:text-base">Nigeria</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Studio visits by appointment</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="card-gradient p-6 sm:p-8 shadow-soft">
              <h3 className="font-display text-lg sm:text-xl font-semibold mb-6">Follow the Journey</h3>
              
              <div className="space-y-4">
                <a 
                  href="https://www.instagram.com/terravia_ceramics?igsh=bmVoZXI2OTVsN21q" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-lg bg-background/50 hover:bg-background/80 transition-smooth hover-lift touch-manipulation"
                >
                  <Instagram className="h-6 w-6 text-primary" />
                  <div>
                    <h4 className="font-medium">Instagram</h4>
                    <p className="text-sm text-muted-foreground">@terravia_ceramics</p>
                  </div>
                </a>
                
                <a 
                  href="https://linkedin.com/in/umar-abdulrasheed-ceramics" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-lg bg-background/50 hover:bg-background/80 transition-smooth hover-lift touch-manipulation"
                >
                  <Linkedin className="h-6 w-6 text-primary" />
                  <div>
                    <h4 className="font-medium">LinkedIn</h4>
                    <p className="text-sm text-muted-foreground">Professional profile</p>
                  </div>
                </a>
              </div>
            </Card>

            <Card className="card-gradient p-6 sm:p-8 shadow-soft">
              <h3 className="font-display text-lg sm:text-xl font-semibold mb-4">Enquiry Types</h3>
              
              <div className="space-y-3">
                <div className="p-3 bg-background/30 rounded-lg">
                  <h4 className="font-medium text-sm">Exhibition Opportunities</h4>
                  <p className="text-xs text-muted-foreground">Gallery partnerships and curatorial projects</p>
                </div>
                
                <div className="p-3 bg-background/30 rounded-lg">
                  <h4 className="font-medium text-sm">Commission Work</h4>
                  <p className="text-xs text-muted-foreground">Custom pieces for private and public spaces</p>
                </div>
                
                <div className="p-3 bg-background/30 rounded-lg">
                  <h4 className="font-medium text-sm">Collaboration</h4>
                  <p className="text-xs text-muted-foreground">Joint projects with artists and institutions</p>
                </div>
                
                <div className="p-3 bg-background/30 rounded-lg">
                  <h4 className="font-medium text-sm">Press & Media</h4>
                  <p className="text-xs text-muted-foreground">Interviews and feature opportunities</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Contact;